import { useEffect, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Settings, Skill } from '@/assets/icons'
import { MenuContent, MenuItemGroup, MenuSeparator, type MenuContentProps } from '@/components/menu/MenuContent'
import { MenuItem } from '@/components/menu/MenuItem'
import { MenuItemContent } from '@/components/menu/MenuItemContent'
import { getMenuItemClassName } from '@/components/menu/menu-item-styles'
import { SelectButton } from '@/components/select-button/SelectButton'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { useClickInsteadOfHover } from '@/components/ui/tooltip/Tooltip'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { UserSkillsLibrary } from '@/user-skills/hooks/useUserSkills'
import { UserSkillSelectionActions } from '@/user-skills/store/selection.slice'
import SkillsDialog from './SkillsDialog'

export default function UserSkillPicker({
  conversationId,
  disabled,
  side = 'top',
  library,
}: {
  conversationId: string
  disabled: boolean
  side?: MenuContentProps['side']
  library: UserSkillsLibrary
}) {
  const [open, setOpen] = useState(false)
  const [managing, setManaging] = useState(false)
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])
  return (
    <>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild disabled={disabled}>
          <SelectButton size="sm" color="neutral" leadingIcon={Skill} aria-label="Skills" hideLabelBelow={400} disabled={disabled}>
            Skills
          </SelectButton>
        </DropdownMenu.Trigger>
        {open && (
          <MenuContent
            side={side}
            align="start"
            collisionPadding={8}
            className="w-80 max-w-[calc(100vw-16px)] max-h-[var(--radix-dropdown-menu-content-available-height)]"
          >
            <CompactSkills conversationId={conversationId} library={library} />
            <MenuSeparator className="shrink-0" />
            <MenuItemGroup className="shrink-0">
              <MenuItem
                label="Manage skills"
                icon={Settings}
                variant="neutral"
                onSelect={() => {
                  setOpen(false)
                  setManaging(true)
                }}
              />
            </MenuItemGroup>
          </MenuContent>
        )}
      </DropdownMenu.Root>
      {managing && <SkillsDialog onClose={() => setManaging(false)} library={library} />}
    </>
  )
}

function CompactSkills({ conversationId, library }: { conversationId: string; library: UserSkillsLibrary }) {
  const dispatch = useAppDispatch()
  const touchOnly = useClickInsteadOfHover()
  const { skills, paxtonSkills, loading, loadError, refresh } = library
  const selection = useAppSelector((state) => state.userSkillSelectionState.byConversationId[conversationId])
  const personalIds = selection?.selectedSkillIds ?? []
  const paxtonNames = selection?.selectedPaxtonSkillNames ?? []
  const rows = [
    ...skills
      .filter((skill) => !personalIds.includes(skill.id))
      .map((skill) => ({
        id: skill.id,
        name: skill.name,
        summary: skill.summary,
        providedByPaxton: false,
        onSelect: () =>
          dispatch(
            UserSkillSelectionActions.selectSkills({
              conversationId,
              skillIds: [...personalIds, skill.id],
              paxtonSkillNames: paxtonNames,
            })
          ),
      })),
    ...paxtonSkills
      .filter((skill) => !paxtonNames.includes(skill.id))
      .map((skill) => ({
        id: skill.id,
        name: skill.name,
        summary: skill.summary,
        providedByPaxton: true,
        onSelect: () =>
          dispatch(
            UserSkillSelectionActions.selectSkills({
              conversationId,
              skillIds: personalIds,
              paxtonSkillNames: [...paxtonNames, skill.id],
            })
          ),
      })),
  ].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
  const libraryHasSkills = skills.length > 0 || paxtonSkills.length > 0
  return (
    <MenuItemGroup className="min-h-0 max-h-[218px] overflow-y-auto">
      {loading && (
        <p role="status" className="p-2 text-sm text-brand-neutral-600">
          Loading skills…
        </p>
      )}
      {loadError && (
        <>
          <p role="alert" className="p-2 text-sm text-brand-neutral-600">
            Couldn’t load skills.
          </p>
          <MenuItem label="Retry" variant="neutral" onSelect={() => void refresh()} />
        </>
      )}
      {!loading && !loadError && rows.map((row) => <CompactSkillRow key={row.id} {...row} showTooltip={!touchOnly} />)}
      {!loading && !loadError && !rows.length && (
        <p className="p-2 text-sm text-brand-neutral-600">
          {libraryHasSkills ? 'All skills active in this chat.' : 'No skills yet. Add one in Manage skills.'}
        </p>
      )}
    </MenuItemGroup>
  )
}

type CompactSkillRowProps = {
  name: string
  summary: string
  providedByPaxton: boolean
  onSelect: () => void
  showTooltip: boolean
}

function CompactSkillRow({ name, summary, providedByPaxton, onSelect, showTooltip }: CompactSkillRowProps) {
  const item = (
    <DropdownMenu.Item
      onSelect={onSelect}
      textValue={name}
      aria-label={providedByPaxton ? `${name} (Paxton)` : name}
      aria-description={summary}
      className={getMenuItemClassName({ variant: 'neutral', className: 'shrink-0 whitespace-normal break-words' })}
    >
      <MenuItemContent icon={Skill} label={name} variant="neutral" />
    </DropdownMenu.Item>
  )

  if (!showTooltip) return item

  return (
    <CustomizableTooltip
      title={summary}
      placement="right-start"
      offset={[0, 0]}
      disableInteractive
      disableTouchListener
      enterDelay={0}
      enterNextDelay={0}
      TransitionProps={{ timeout: 0 }}
      tooltipClassName="!m-0 !max-w-[244px] !rounded-xs !bg-brand-neutral-800 !px-1 !py-0.5 !font-sans !text-[10.4px] !font-medium !leading-[10.4px] !tracking-[0.125px] !text-white"
    >
      {item}
    </CustomizableTooltip>
  )
}
